export const theme_modes = ['light', 'dark'] as const;
export const theme_scopes = ['portal', 'public'] as const;

export type ThemeMode = (typeof theme_modes)[number];
export type ThemeScope = (typeof theme_scopes)[number];

export const theme_token_keys = [
  'primary',
  'secondary',
  'tertiary',
  'neutral',
  'primaryForeground',
  'background',
  'surface',
  'surfaceMuted',
  'foreground',
  'foregroundMuted',
  'border',
  'accent',
  'success',
  'warning',
  'danger',
  /**
   * UPT-1968 — l'information a sa propre couleur, indépendante de la marque.
   *
   * Elle n'en avait pas : les messages d'information retombaient sur la
   * couleur du thème. Un tenant dont la marque est rouge voyait donc ses
   * informations en rouge — indiscernables d'une erreur, ce qui est le
   * contraire de ce qu'une couleur d'état doit faire. `info` rejoint
   * `success`, `warning` et `danger` : une famille d'états, que la marque
   * ne traverse pas.
   */
  'info',
  'color1',
  'color2',
  'color3',
  'color4',
  'color5',
  'color6',
  'color7',
  'color8',
  'color9',
  'color10',
  /** 23.10.2026 (Olivier) — couleur de base du fond du Studio (scène Géode), réglée par mode dans le thème du tenant. */
  'studioBackground',
] as const;

export type ThemeTokenKey = (typeof theme_token_keys)[number];
export type ThemeTokens = Partial<Record<ThemeTokenKey, string>>;

export type ThemeAssetConfiguration = {
  logoLightUrl?: string | null;
  logoDarkUrl?: string | null;
  faviconUrl?: string | null;
};

export type ThemeTypographyConfiguration = {
  displayFontFamily?: string | null;
  bodyFontFamily?: string | null;
  labelFontFamily?: string | null;
};

export type ThemeShapeConfiguration = {
  cornerRadius?: number | null;
};

export type ThemeModeConfiguration = {
  tokens?: ThemeTokens | null;
  assets?: ThemeAssetConfiguration | null;
  typography?: ThemeTypographyConfiguration | null;
  shape?: ThemeShapeConfiguration | null;
};

export type ThemeScopeConfiguration = {
  modes?: Partial<Record<ThemeMode, ThemeModeConfiguration>> | null;
};

export type TenantThemeConfiguration = {
  scopes?: Partial<Record<ThemeScope, ThemeScopeConfiguration>> | null;
};

export type TenantThemeSource = 'PLATFORM_DEFAULT' | 'TENANT_OVERRIDE';

export type ThemeCatalogItem = {
  themeKey: string;
  themeName: string;
  selected: boolean;
  tenantDefault: boolean;
  source: TenantThemeSource | null;
};

export type TenantThemeResponse = {
  tenantId: string;
  version: number | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  source?: TenantThemeSource | null;
  selectedThemeKey?: string | null;
  selectedThemeName?: string | null;
  tenantDefaultThemeKey?: string | null;
  availableThemes?: ThemeCatalogItem[];
  configuration: TenantThemeConfiguration;
};

export type TenantThemeCatalogResponse = {
  tenantId: string;
  version: number | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  selectedThemeKey?: string | null;
  selectedThemeName?: string | null;
  tenantDefaultThemeKey?: string | null;
  themes: ThemeCatalogItem[];
};

export type UserThemePreferenceResponse = {
  tenantId: string;
  themeKey: string | null;
  themeName?: string | null;
  tenantDefaultThemeKey?: string | null;
  availableThemes: ThemeCatalogItem[];
};

export type ThemePresetRecord = {
  id: string;
  name: string;
  configuration: TenantThemeConfiguration;
  createdAt: string;
  updatedAt: string;
};

export type TenantThemeUpdateRequest = {
  version?: number | null;
  themeKey?: string | null;
  themeName?: string | null;
  setAsTenantDefault?: boolean;
  configuration: TenantThemeConfiguration;
};

export type UserThemePreferenceUpdateRequest = {
  themeKey: string;
};

export type ResolvedThemeModeConfiguration = {
  tokens: Record<ThemeTokenKey, string>;
  assets: ThemeAssetConfiguration;
  typography: Required<ThemeTypographyConfiguration>;
  shape: Required<ThemeShapeConfiguration>;
};

export const is_theme_mode = (value: string | null | undefined): value is ThemeMode =>
  value === 'light' || value === 'dark';

export const is_theme_scope = (value: string | null | undefined): value is ThemeScope =>
  value === 'portal' || value === 'public';
